import React, { Ref, useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';
import Button from '@alfa-retail-react/components/button';
import {
    MainScreen,
    OverContentLoadableBlock,
    PageFrame,
    ShowComponent,
    useForm,
    useNavigate,
} from '@alfa-retail-react/components';
import { YesNoState } from '@alfa-retail-react/components/yes-no-question';

import { EmploymentSkeleton } from 'app/component/page/skeletons';
import { validationExceptionHandler } from 'app/logic/pages/error/validationExceptionHandler';
import {
    unauthorizedExceptionHandlerOnLoadData,
    unauthorizedExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/unauthorizedExceptionHandler';
import { StepOrder, TOTAL_STEP_COUNT } from 'app/constants/step';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import {
    networkExceptionHandlerOnLoadData,
    networkExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/networkExceptionHandler';
import { StepNames } from 'app/StepNames';
import { AddressFormValues } from 'app/logic/address/AddressForm';
import { Step } from 'app/component/step/Step';
import { EmploymentPageStore } from 'app/logic/pages/employment/EmploymentPageStore';
import { OrderService } from 'app/service/OrderService';
import { Reference } from 'app/model/Reference';
import { Employment } from 'app/model/Employment';
import { PageErrors } from 'app/logic/pages/error/PageErrors';
import { LocalityAddress } from 'app/model/LocalityAddress';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { SoleOwnerFormSection } from 'app/logic/pages/employment/SoleOwnerFormSection';
import { JobDetailsFormSection } from 'app/logic/pages/employment/JobDetailsFormSection';
import { DeliveryAddressFormSection } from 'app/logic/pages/employment/DeliveryAddressFormSection';
import { useAnalytics } from 'app/analytics/useAnalytics';
import { orderRejectedExceptionHandler } from 'app/logic/pages/error/orderRejectedExceptionHandler';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';
import { PageNames } from 'app/PageNames';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import { ProductParametersConfigureFormStore } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigureFormStore';
import { useI18nContext } from 'app/store/сontext/I18nContext';

import 'app/logic/pages/employment/EmploymentPage.less';

export type EmploymentFormValues = {
    activityKindCode: Reference;
    soleOwner: YesNoState;
    sameAddress: YesNoState;
    unp: string;
    incomeAmount: number;
    activityFieldCode: string;
    experience: string;

    deliveryAddress: AddressFormValues;
};

const CURRENT_STEP_ORDER = StepOrder.STEP_JOB_DATA;
const PAGE_NAME = 'EmploymentPage';

/**
 * Страница ввода сведений о работе.
 */
export const EmploymentPage = observer(() => {
    const applicationContext = useApplicationContext();
    const navigate = useNavigate();

    const pageStore = useRef(new EmploymentPageStore()).current;
    const { formStore, order } = pageStore;
    const analytics = useAnalytics(
        applicationContext.productCode,
        PAGE_NAME,
        false,
    );
    const notification = useNotificationContext();
    const i18nContext = useI18nContext();

    const form = useForm<EmploymentFormValues>({
        errorContextConverter: pageStore.contextConverter,
    });
    const {
        handleSubmitWithValidationError,
        pageErrors,
        setPageErrors,
        pageErrorsComponentRef,
    } = form;

    useEffect(() => {
        analytics.pageDisplayed();

        const orderId = sessionStorage.getItem('orderId');
        pageStore
            .initialize(orderId)
            .then(() => {
                if (
                    applicationContext.isFormRestoreAvailable(
                        CURRENT_STEP_ORDER,
                    )
                ) {
                    pageStore.restoreFormData(
                        applicationContext.latestFilledFormData,
                    );
                }
            })
            .catch(exceptionLogger(PAGE_NAME, 'initialize'))
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(networkExceptionHandlerOnLoadData({ navigate }))
            .catch(defaultExceptionHandler(notification, analytics));
    }, []);

    const backAction = () => {
        analytics.back();

        applicationContext.setLatestFilledFormData(
            pageStore.restorationFormData,
            CURRENT_STEP_ORDER,
        );

        navigate(
            `/${
                applicationContext.productCode
            }/order?orderId=${sessionStorage.getItem('orderId')}`,
            { refererPage: PageNames.EMPLOYMENT_PAGE_NAME },
        );
    };

    const onSubmitForm = () => {
        const activityKindCode =
            formStore.soleOwner !== YesNoState.YES
                ? Employment.ActivityCode.default
                : formStore.activityKindCode;

        /**
         * Установка лимита в максимум в случае, если клиент перешел назад по приложению и изменил тип деятельности
         */
        const isCreditLimitSet =
            applicationContext.latestFilledFormData.creditLimit;
        const isActivityKindCodeChanged =
            order.client.employment &&
            order.client.employment.activityKindCode !== activityKindCode;

        if (isActivityKindCodeChanged) {
            const maxAmount = ProductParametersConfigureFormStore.getMaxAmount({
                cardProduct: applicationContext.cardProduct,
                activityKindCode,
            });

            if (isCreditLimitSet) {
                applicationContext.updateLatestFilledFormData({
                    creditLimit: String(maxAmount),
                });
            }

            if (order.amount) {
                order.amount = maxAmount;
            }
        }

        const unp = formStore.availableUNP ? formStore.unp : null;
        const incomeAmount = formStore.incomeAmount;
        const activityFieldCode = !formStore.isUnemployedPensioner
            ? formStore.activityFieldCode
            : null;
        const experience = !formStore.isUnemployedPensioner
            ? formStore.experience
            : null;

        order.client.employment = new Employment(
            activityKindCode,
            unp,
            incomeAmount,
            activityFieldCode,
            experience,
        );

        order.deliveryAddress =
            formStore.sameAddress === YesNoState.YES
                ? order.client.residentialAddress
                : formStore.deliveryAddressFormStore.getModel();

        //todo: выяснить может ли изменяться страна адреса доставки.
        order.deliveryAddress.countryCode =
            LocalityAddress.BELARUS_COUNTRY_CODE;

        order.stage = StepNames.STEP_JOB_DATA;

        return pageStore.actionStateStore.loadData(() => {
            analytics.confirm();
            return new OrderService()
                .updateOrder(order)
                .then(() => {
                    applicationContext.latestSubmittedStep = CURRENT_STEP_ORDER;
                    navigate(
                        `/${applicationContext.productCode}/product-parameters-configure`,
                    );
                })
                .catch(exceptionLogger(PAGE_NAME, 'orderService.updateOrder'))
                .catch(
                    unauthorizedExceptionHandlerOnSubmitData({
                        navigate,
                        phoneNumber: applicationContext.phoneNumber,
                    }),
                )
                .catch(
                    orderRejectedExceptionHandler(
                        navigate,
                        analytics,
                        i18nContext.t,
                    ),
                )
                .catch(
                    validationExceptionHandler(
                        form,
                        analytics,
                        pageStore.contextConverter,
                    ),
                )
                .catch(networkExceptionHandlerOnSubmitData({ setPageErrors }))
                .catch(defaultExceptionHandler(notification, analytics));
        });
    };

    return (
        <OverContentLoadableBlock show={pageStore.actionStateStore.isLoading}>
            <PageFrame
                rightAddonLogo={applicationContext.additionalLogo}
                canGoPreviousPage
                backAction={backAction}
                displayFooter
            >
                <MainScreen>
                    <MainScreen.Header
                        title={applicationContext.getCardProductTitle()}
                    />
                    <MainScreen.ContentBar>
                        <div className="employmentPage">
                            <ShowComponent
                                condition={pageStore.loadStateStore.isLoaded}
                                alternative={<EmploymentSkeleton />}
                            >
                                <>
                                    <Step
                                        current={CURRENT_STEP_ORDER}
                                        total={TOTAL_STEP_COUNT}
                                    />
                                    <FormProvider<EmploymentFormValues>
                                        {...form}
                                    >
                                        <form
                                            onSubmit={handleSubmitWithValidationError(
                                                {
                                                    onValid: onSubmitForm,
                                                },
                                            )}
                                        >
                                            <SoleOwnerFormSection
                                                pageStore={pageStore}
                                            />
                                            <JobDetailsFormSection
                                                pageStore={pageStore}
                                            />
                                            <DeliveryAddressFormSection
                                                pageStore={pageStore}
                                            />

                                            {pageErrors.length > 0 && (
                                                <PageErrors
                                                    ref={
                                                        pageErrorsComponentRef as Ref<HTMLDivElement>
                                                    }
                                                    errors={pageErrors}
                                                />
                                            )}
                                            <Button
                                                className="submitAction"
                                                type="submit"
                                                view="accent"
                                                disabled={
                                                    pageStore.actionStateStore
                                                        .isLoading
                                                }
                                            >
                                                Подтвердить
                                            </Button>
                                        </form>
                                    </FormProvider>
                                </>
                            </ShowComponent>
                        </div>
                    </MainScreen.ContentBar>
                </MainScreen>
            </PageFrame>
        </OverContentLoadableBlock>
    );
});
